import { Link } from "@remix-run/react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

type ErrorAction = {
	label: string;
	to?: string;
	onClick?: () => void;
	variant?: "default" | "outline";
};

interface Error9Props {
	errorCode?: string;
	title?: string;
	description?: string;
	contactButtonText?: string;
	contactButtonLink?: string;
	homeButtonText?: string;
	homeButtonLink?: string;
	primaryAction?: ErrorAction;
	secondaryAction?: ErrorAction;
	details?: Record<string, string | undefined>;
	stack?: string;
	raw?: string;
	fullScreen?: boolean;
}

function formatLabel(label: string) {
	return label
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/[_-]+/g, " ")
		.replace(/\b\w/g, (match) => match.toUpperCase());
}

function renderAction(action: ErrorAction, key: string) {
	if (action.to) {
		return (
			<Button
				key={key}
				variant={action.variant ?? "default"}
				size="lg"
				className="font-medium"
				asChild
			>
				<Link to={action.to}>{action.label}</Link>
			</Button>
		);
	}

	return (
		<Button
			key={key}
			variant={action.variant ?? "default"}
			size="lg"
			className="font-medium"
			onClick={action.onClick}
		>
			{action.label}
		</Button>
	);
}

const Error9 = ({
	errorCode = "404",
	title = "Whoops.. Page Not Found !!!",
	description = "Don't worry, it's on our end. Please try again in a bit or contact us if the issue sticks around.",
	contactButtonText = "Contact us",
	contactButtonLink = "/contact",
	homeButtonText = "Go Home",
	homeButtonLink = "/",
	primaryAction,
	secondaryAction,
	details,
	stack,
	raw,
	fullScreen = true
}: Error9Props) => {
	const actions = [
		secondaryAction ?? {
			label: contactButtonText,
			to: contactButtonLink,
			variant: "outline" as const
		},
		primaryAction ?? {
			label: homeButtonText,
			to: homeButtonLink,
			variant: "default" as const
		}
	].filter(Boolean);

	const visibleDetails = Object.entries(details ?? {}).filter(
		([, value]) => value && value.trim().length > 0
	);

	return (
		<div
			className={cn(
				"relative flex w-full items-center justify-center overflow-hidden bg-background py-16",
				fullScreen ? "min-h-screen" : "min-h-[calc(100vh-8rem)] rounded-2xl border"
			)}
		>
			<div className="absolute inset-0 h-full w-full">
				<div className="absolute left-20 top-20 size-8 rotate-45 border border-violet-600/60" />
				<div className="absolute right-40 top-40 size-16 rotate-45 border border-violet-600/60" />
				<div className="absolute bottom-32 left-1/4 size-12 -rotate-12 transform border border-violet-600/60" />
				<div className="absolute right-1/3 top-1/4 size-10 rotate-12 transform border border-violet-600/60" />
				<div className="absolute bottom-20 right-20 size-12 rotate-45 transform border border-violet-600/60" />
				<div className="absolute left-1/3 top-1/3 size-20 -rotate-12 transform border border-violet-600/60" />
			</div>

			<div className="relative z-10 w-full max-w-4xl space-y-6 px-4 text-center">
				<div className="space-y-3">
					<h1 className="text-7xl font-bold tracking-tight text-foreground sm:text-8xl">
						{errorCode}
					</h1>
					<h2 className="text-xl font-semibold text-foreground">{title}</h2>
					<p className="mx-auto max-w-2xl text-sm text-muted-foreground sm:text-base">
						{description}
					</p>
				</div>

				<div className="mx-auto max-w-3xl rounded-xl border bg-background/80 p-4 text-left shadow-sm backdrop-blur">
					<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
						What Happened
					</p>
					<p className="mt-2 font-mono text-sm text-foreground">{title}</p>
					<p className="mt-2 font-mono text-sm text-muted-foreground">
						{raw || description}
					</p>
				</div>

				<div className="flex flex-wrap items-center justify-center gap-4 pt-2">
					{actions.map((action, index) => renderAction(action, `${action.label}-${index}`))}
				</div>

				<details className="mx-auto max-w-3xl rounded-xl border bg-muted/40 text-left shadow-sm">
					<summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-foreground">
						Developer Information
					</summary>
					<div className="space-y-4 border-t px-4 py-4">
						<div className="grid gap-3 sm:grid-cols-2">
							<div className="rounded-lg border bg-background/80 p-3">
								<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
									Error Code
								</p>
								<p className="mt-2 font-mono text-sm text-foreground">
									{errorCode}
								</p>
							</div>
							<div className="rounded-lg border bg-background/80 p-3">
								<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
									User-Facing Message
								</p>
								<p className="mt-2 font-mono text-sm text-foreground">
									{description}
								</p>
							</div>
							{visibleDetails.map(([key, value]) => (
								<div
									key={key}
									className="rounded-lg border bg-background/80 p-3"
								>
									<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
										{formatLabel(key)}
									</p>
									<p className="mt-2 break-all font-mono text-sm text-foreground">
										{value}
									</p>
								</div>
							))}
						</div>

						{raw ? (
							<div className="rounded-lg border bg-background/80 p-3">
								<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
									Raw Error
								</p>
								<pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs text-foreground">
									{raw}
								</pre>
							</div>
						) : null}

						{stack ? (
							<div className="rounded-lg border bg-background/80 p-3">
								<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
									Stack Trace
								</p>
								<pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-foreground">
									{stack}
								</pre>
							</div>
						) : null}
					</div>
				</details>
			</div>
		</div>
	);
};

export { Error9 };
